import { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Typography, IconButton, Grid, createTheme, ThemeProvider } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { RoleEnum } from '../../types/enums';
/**
 * @remarks This React component handles the logic as well as look and feel of a
 * drag and drop/click file upload box. It also allows the user to download a submission.
 * @param - object with properties and functions required for Component to work.
 * @returns - Html component for uploading a file.
 */
function DragDropFileUpload({ onFileUpload, submissionStage, userRole, myFile, title }) {
    const [dragOver, setDragOver] = useState(false);
    const [fileTitle, setFileTitle] = useState<string | null>(title);
    const [filePdf, setFilePdf] = useState(null);

    const theme = createTheme({
        palette: {
            primary: {
                main: '#10AD61'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        setDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (event) => {
            event.preventDefault();
            setDragOver(false);
            const files = event.dataTransfer.files;
            if (files && files[0]) {
                handleFileChange(files[0]);
            }
        },
        []
    );

    const handleFileChange = (file) => {
        onFileUpload(file);
        setFilePdf(file);
        setFileTitle(file.name);
    }

    const handleChange = useCallback(
        (event) => {
            const files = event.target.files;
            if (files && files[0]) {
                handleFileChange(files[0]);
            }
        },
        [filePdf]
    );

    const handleDownloadClick = () => {
        if (filePdf) {
            const blob = new Blob([filePdf], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileTitle as string;
            anchor.click();
            URL.revokeObjectURL(url);
        }
    }

    useEffect(() => {
        setFilePdf(myFile);
    }, [myFile]
    )

    return (
        <Box>
            <ThemeProvider theme={theme}>
                <Paper
                    variant="outlined"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        border: dragOver ? '2px dashed #000' : '2px dashed #aaa',
                        padding: 20,
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: dragOver ? '#eee' : '#fafafa',
                        position: 'relative',
                    }}
                >
                    {fileTitle && filePdf ? (
                        <Grid container justifyContent={"center"} style={{ marginTop: "2.8vh", marginBottom: "2.8vh" }} alignItems={"center"}>
                            <Grid item xs={12}>
                                <Grid container direction={"row"} alignItems={"center"} justifyContent={"center"} rowSpacing={3}>
                                    <Grid item xs={11}>
                                        <Typography onClick={handleDownloadClick} style={{ cursor: 'pointer' }}>
                                            {fileTitle}
                                        </Typography>
                                    </Grid>
                                    {submissionStage === 'Not Started' || submissionStage === 'Draft In-Progress' &&
                                        (userRole === RoleEnum.Youth || userRole <= RoleEnum.SystemAdministrator) ?
                                        <Grid item xs={1}>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => { setFileTitle("") }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Grid>
                                        :
                                        <Grid item xs={12}>
                                            <Typography variant='button' onClick={handleDownloadClick}>
                                                click to download submission
                                            </Typography>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    ) :
                        <><input
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={handleChange} /><label htmlFor="raised-button-file">
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <IconButton color="primary" aria-label="upload picture" component="span">
                                        <CloudUploadIcon style={{ fontSize: 60 }} />
                                    </IconButton>
                                    <Typography>Drag and drop files here or click to select files</Typography>
                                </Box>
                            </label></>
                    }
                </Paper>
            </ThemeProvider>
        </Box>
    );
}
export default DragDropFileUpload;